import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Facebook, Instagram, Twitter, Linkedin, ArrowUpRight, Sparkles } from "lucide-react";
import { API } from "../auth/AuthContext";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import { FOOTER_TAGLINE, FOOTER_NEWSLETTER_HINT } from "../lib/softLaunch";
import { SITE_SOCIALS } from "../lib/siteConfig";

const SOCIAL_ICONS = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
};

const COLUMNS = [
  {
    heading: "Drive",
    items: [
      { label: "Browse the fleet", hint: "Curated vehicles from verified hosts", to: "/browse" },
      { label: "Find parking", hint: "Hourly and monthly spots", to: "/parking" },
      { label: "How it works", hint: "Search, book, drive", to: "/#how-it-works" },
    ],
  },
  {
    heading: "Earn",
    items: [
      { label: "Become a host", hint: "List a car or driveway", to: "/host" },
      ...(FRACTIONAL_ENABLED ? [{ label: "Invest", hint: "Fractional vehicle shares", to: "/invest" }] : []),
      { label: "Rewards", hint: "Earn ZCT on every trip", to: "/loyalty" },
    ],
  },
  {
    heading: "Trust & support",
    items: [
      { label: "Safety & insurance", hint: "Coverage on every trip", to: "/safety" },
      { label: "Coverage details", hint: "What’s included on your rental", to: "/coverage" },
      { label: "Help center", hint: "Answers in under a minute", to: "/help" },
      { label: "Cancellation policy", hint: "Flexible trip changes", to: "/cancellation" },
      { label: "Terms & privacy", hint: "Legal and data use", to: "/terms" },
    ],
  },
];

function FooterLink({ item }) {
  const isHash = item.to?.startsWith("/#");
  const label = (
    <div className="group block py-2 cursor-pointer" data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-sm text-[var(--zc-bg-2)] group-hover:text-white font-semibold transition">
        {item.label}
        <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
      </div>
      {item.hint && (
        <div className="text-xs text-white/45 mt-0.5 leading-snug group-hover:text-[var(--zc-bg-2)] transition">
          {item.hint}
        </div>
      )}
    </div>
  );
  if (isHash) {
    return (
      <a href={item.to} onClick={(e) => {
        e.preventDefault();
        const id = item.to.split("#")[1];
        if (window.location.pathname !== "/") {
          window.location.href = item.to;
          return;
        }
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}>
        {label}
      </a>
    );
  }
  return <Link to={item.to}>{label}</Link>;
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const onNewsletterSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus({ type: "error", message: "Enter a valid email address." });
      return;
    }

    setSubmitting(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/interest/phase2`, {
        email: trimmed,
        source: "footer_stay_in_loop",
      });
      if (res.data?.ok) {
        setEmail("");
        setStatus({
          type: "success",
          message: res.data.deduplicated
            ? "You're already on the list — we'll be in touch."
            : "You're on the list — check your inbox for a confirmation.",
        });
      } else {
        setStatus({ type: "error", message: "Something went wrong. Please try again." });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        type: "error",
        message: typeof detail === "string" ? detail : "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[var(--zc-text)] text-white mt-12" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.25fr_1fr_1fr_1fr] gap-8 lg:gap-10">
        <div className="min-w-0 md:col-span-2 lg:col-span-1">
          <Link
            to="/"
            className="inline-block mb-3 min-w-0 max-w-xs"
            data-testid="footer-brand"
            aria-label="Z-Cars home"
          >
            <img
              src="/brand/wordmark.png"
              alt="Z-Cars"
              className="h-7 sm:h-8 w-auto max-w-full select-none"
              draggable="false"
            />
          </Link>

          <p className="zc-headline text-lg text-[var(--zc-bg-2)] leading-snug max-w-sm">
            Smart mobility.{" "}
            <span className="text-[var(--zc-accent)] italic">
              {FRACTIONAL_ENABLED ? "Smart investment." : "Verified hosts."}
            </span>
          </p>

          <div className="mt-6 max-w-md">
            <form className="w-full min-w-0" onSubmit={onNewsletterSubmit} noValidate>
              <div className="eyebrow text-white/55 mb-1.5">Stay in the loop</div>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="you@email.com"
                  aria-label="Email for newsletter"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  required
                  className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-md px-3 h-9 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/40 disabled:opacity-60"
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 h-9 bg-[var(--zc-bg-2)] text-[var(--zc-text)] rounded-md text-xs font-bold tracking-wide hover:bg-[var(--zc-bg-3)] transition disabled:opacity-60 shrink-0"
                  data-testid="footer-newsletter-cta"
                >
                  {submitting ? "Saving…" : "Notify me"}
                </button>
              </div>
              <p className="text-caption text-white/55 mt-1.5 leading-snug">
                <Sparkles size={10} className="inline text-[var(--zc-accent)] shrink-0 mr-1.5 align-[-0.1em]" aria-hidden />
                {FOOTER_NEWSLETTER_HINT}
              </p>
              {status && (
                <p
                  className={`text-caption mt-1.5 ${status.type === "success" ? "text-[var(--zc-success)]" : "text-[var(--zc-danger)]"}`}
                  data-testid="footer-newsletter-status"
                  role="status"
                >
                  {status.message}
                </p>
              )}
            </form>
          </div>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.heading} className="min-w-0">
            <div className="eyebrow text-white/55 mb-3">{col.heading}</div>
            <ul className="space-y-0.5">
              {col.items.map((item) => (
                <li key={item.label}>
                  <FooterLink item={item} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-center justify-between text-xs text-white/55 gap-3">
          <div>© 2026 <span className="zc-wordmark font-semibold text-[var(--zc-bg-2)]">Z-Cars</span>. All rights reserved.</div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <div className="flex items-center gap-2">
              {SITE_SOCIALS.map((social) => {
                const Icon = SOCIAL_ICONS[social.id];
                if (!Icon) return null;
                return (
                  <a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 grid place-items-center border border-white/15 rounded-full hover:border-white transition text-[var(--zc-bg-2)]"
                    aria-label={social.label}
                    data-testid={`footer-social-${social.id}`}
                  >
                    <Icon size={12} />
                  </a>
                );
              })}
            </div>
            <span className="hidden sm:inline">{FOOTER_TAGLINE} · KYC-verified · Escrow-protected</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
